import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { Folder, Upload, Download, Eye, Trash2, File, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import './DocumentsPage.css';

/**
 * Documents page - Document management with upload/download.
 */
export const DocumentsPage = () => {
  const { user } = useEntitlements();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [clients, setClients] = useState([]);
  
  // Sorting and filtering state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterClient, setFilterClient] = useState('');
  const [filterDocumentType, setFilterDocumentType] = useState('');
  
  const [uploadData, setUploadData] = useState({
    file: null,
    name: '',
    description: '',
    document_type: 'general',
    client: '',
    visibility: 'staff_only',
  });
  
  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';
  const isClient = user?.is_client_user || user?.role === 'client';

  useEffect(() => {
    fetchDocuments();
    if (isStaff) {
      fetchClients();
    }
  }, [isStaff]);
  
  useEffect(() => {
    // Apply filters and sorting
    let filtered = [...documents];
    
    // Apply client filter
    if (filterClient) {
      filtered = filtered.filter(doc => doc.client === filterClient);
    }
    
    // Apply document type filter
    if (filterDocumentType) {
      filtered = filtered.filter(doc => doc.document_type === filterDocumentType);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'file_size':
          aVal = a.file_size || 0;
          bVal = b.file_size || 0;
          break;
        case 'client':
          aVal = a.client_name || '';
          bVal = b.client_name || '';
          break;
        case 'document_type':
          aVal = a.document_type || '';
          bVal = b.document_type || '';
          break;
        default:
          aVal = a[sortField] || '';
          bVal = b[sortField] || '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredDocuments(filtered);
  }, [documents, sortField, sortDirection, filterClient, filterDocumentType]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/documents/');
      setDocuments(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await api.get('/v1/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file, name: uploadData.name || file.name });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('name', uploadData.name || uploadData.file.name);
      formData.append('document_type', uploadData.document_type);
      
      // For client users, set visibility to 'client' and auto-assign to their client
      if (isClient && user?.client?.id) {
        formData.append('visibility', 'client');
        formData.append('client', user.client.id);
      } else {
        formData.append('visibility', uploadData.visibility);
        if (uploadData.client) {
          formData.append('client', uploadData.client);
        }
      }
      
      if (uploadData.description) {
        formData.append('description', uploadData.description);
      }

      await api.post('/v1/documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setShowUploadModal(false);
      setUploadData({
        file: null,
        name: '',
        description: '',
        document_type: 'general',
        client: '',
        visibility: 'staff_only',
      });
      setError(null);
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const handleDownload = async (document) => {
    try {
      const response = await api.get(`/v1/documents/${document.id}/file/`);
      // Use the pre-signed URL from the response to download the file
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        setError('Download URL not available');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to download document');
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Delete "${document.name}"?`)) return;

    try {
      await api.delete(`/v1/documents/${document.id}/`);
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="documents-page">
        <div className="loading">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <PageHeader
        icon={Folder}
        title="Documents"
        subtitle="Upload, organize, and share files across your portal."
        primaryAction={(isStaff || isClient) ? (
          <button
            className="btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={16} />
            Upload Document
          </button>
        ) : null}
      />
      
      {/* Filters and Sorting */}
      <div className="documents-filters">
        {isStaff && (
          <div className="filter-group">
            <label>Filter by Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="filter-group">
          <label>Filter by Type</label>
          <select
            value={filterDocumentType}
            onChange={(e) => setFilterDocumentType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="general">General</option>
            <option value="contract">Contract</option>
            <option value="invoice">Invoice</option>
            <option value="report">Report</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort By</label>
          <select
            value={`${sortField}_${sortDirection}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('_');
              setSortField(field);
              setSortDirection(direction);
            }}
          >
            <option value="created_at_desc">Newest First</option>
            <option value="created_at_asc">Oldest First</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="file_size_desc">Largest First</option>
            <option value="file_size_asc">Smallest First</option>
            {isStaff && (
              <>
                <option value="client_asc">Client (A-Z)</option>
                <option value="client_desc">Client (Z-A)</option>
              </>
            )}
            <option value="document_type_asc">Type (A-Z)</option>
            <option value="document_type_desc">Type (Z-A)</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {uploading && (
        <div className="upload-banner">Uploading document...</div>
      )}

      <div className="documents-list">
        {filteredDocuments.length === 0 ? (
          <div className="portalr-emptyState">
            <div className="portalr-emptyStateIcon" aria-hidden="true">
              <Folder size={20} />
            </div>
            <div className="portalr-emptyStateTitle">
              {documents.length === 0 ? 'No documents yet' : 'No matches found'}
            </div>
            <p className="portalr-emptyStateDesc">
              {documents.length === 0
                ? 'Upload files to begin sharing with clients.'
                : 'Try adjusting filters or sorting to find what you need.'}
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {documents.length === 0 ? (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => setShowUploadModal(true)}
                >
                  Upload your first file
                </button>
              ) : (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--secondary"
                  onClick={() => {
                    setFilterClient('');
                    setFilterDocumentType('');
                    setSortField('created_at');
                    setSortDirection('desc');
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={`documents-table-header ${isStaff ? 'with-client' : 'no-client'}`}>
              <div className="table-header-cell" onClick={() => handleSort('name')}>
                Name {getSortIcon('name')}
              </div>
              {isStaff && (
                <div className="table-header-cell" onClick={() => handleSort('client')}>
                  Client {getSortIcon('client')}
                </div>
              )}
              <div className="table-header-cell" onClick={() => handleSort('document_type')}>
                Type {getSortIcon('document_type')}
              </div>
              <div className="table-header-cell" onClick={() => handleSort('file_size')}>
                Size {getSortIcon('file_size')}
              </div>
              <div className="table-header-cell" onClick={() => handleSort('created_at')}>
                Uploaded {getSortIcon('created_at')}
              </div>
              <div className="table-header-cell">Actions</div>
            </div>
            <div className="documents-table">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className={`document-row ${isStaff ? 'with-client' : 'no-client'}`}>
                  <div className="table-cell">
                    <div className="document-name-cell">
                      <File size={16} />
                      <span>{doc.name}</span>
                    </div>
                    {doc.description && (
                      <p className="document-description">{doc.description}</p>
                    )}
                  </div>
                  {isStaff && (
                    <div className="table-cell">
                      {doc.client_name || <span className="text-muted">No client</span>}
                    </div>
                  )}
                  <div className="table-cell">
                    <span className="document-type-badge">{doc.document_type}</span>
                  </div>
                  <div className="table-cell">
                    {doc.file_size_mb} MB
                  </div>
                  <div className="table-cell">
                    <div>
                      {new Date(doc.created_at).toLocaleDateString()}
                    </div>
                    {doc.uploaded_by_name && (
                      <div className="text-muted small">
                        by {doc.uploaded_by_name}
                      </div>
                    )}
                  </div>
                  <div className="table-cell">
                    <div className="document-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                      {isStaff && (
                        <button
                          className="btn-icon"
                          onClick={() => handleDelete(doc)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Upload Document</h2>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>File *</label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  required
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={uploadData.name}
                  onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                  required
                  placeholder="Document name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows="3"
                  placeholder="Optional description"
                />
              </div>
              <div className="form-group">
                <label>Document Type</label>
                <select
                  value={uploadData.document_type}
                  onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                >
                  <option value="general">General</option>
                  <option value="contract">Contract</option>
                  <option value="invoice">Invoice</option>
                  <option value="report">Report</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {isStaff && (
                <>
                  <div className="form-group">
                    <label>Assign to Client (Optional)</label>
                    <select
                      value={uploadData.client}
                      onChange={(e) => setUploadData({ ...uploadData, client: e.target.value })}
                    >
                      <option value="">No client assignment</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Visibility</label>
                    <select
                      value={uploadData.visibility}
                      onChange={(e) => setUploadData({ ...uploadData, visibility: e.target.value })}
                    >
                      <option value="staff_only">Staff Only</option>
                      <option value="client">Client Visible</option>
                      <option value="public">Public</option>
                    </select>
                  </div>
                </>
              )}
              {isClient && user?.client && (
                <div className="form-group">
                  <p className="text-muted small">
                    This document will be associated with {user.client.company_name || user.client.name || 'your client account'} and visible to staff.
                  </p>
                </div>
              )}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadData({
                      file: null,
                      name: '',
                      description: '',
                      document_type: 'general',
                      client: '',
                      visibility: 'staff_only',
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={uploading}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

